import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Button, TextField } from "@mui/material";
import { useFormik } from "formik";
import axios from "axios";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
};

function PlaceOrderInventory({ selectedInventory, handleClose, getData }) {
  const [buyerOptions, setBuyerOptions] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getBuyers();
  }, []);

  async function getBuyers() {
    try {
      const url = `/api/parents/orders?page=1&limit=10000&status=Approved`;

      const response = await axios({
        method: "GET",
        url: url,
      });
      setBuyers(response.data.parent_orders.rows);
      setBuyerOptions(
        response.data.parent_orders.rows?.map((u) => ({
          value: Number(u.id),
          label: u.id,
        }))
      );
    } catch {
      // Error loding buyers
      console.log("Error loading buyers");
    }
  }

  const handleSubmit = async (values) => {
    try {
      let buyerUserId = buyers?.find(
        (b) => b.id === values.selectedBuyer
      )?.user_id;
      buyerUserId = Number(buyerUserId);

      if(!buyerUserId || isNaN(buyerUserId)) {
        return toast.error('Invalid buyer, select another parent with valid buyer');
      }
      setIsSubmitting(true);
      const cart = await axios({
        method: "POST",
        url: `/v11/merchant/${buyerUserId}/cart`,
        data: {
          cart_items: [
            {
              color_size_variant: "",
              measurement_unit: selectedInventory?.product?.measurement_unit,
              pack_sizes: selectedInventory?.product?.new_pack_size,
              product_cities: "",
              product_id: Number(selectedInventory?.product_id),
              new_quantity: values.quantity,
              shipping_status: "Pending", //static
            },
          ],
          user_id: buyerUserId,
        },
      });
      if (cart?.data?.data?.ID) {
        const amount = values.quantity * values.price;
        const gst =
          (amount / 100) * (Number(selectedInventory?.product?.gst) || 0);
        const cess = Number(selectedInventory?.product?.cess_amount) || 0;
        const order = await axios({
          method: "POST",
          url: "/api/orders",
          data: {
            cart_id: cart?.data?.data?.ID,
            user_id: buyerUserId,
            shipping_address: selectedInventory?.buyer?.shop?.shipping_address,
            billing_address: selectedInventory?.buyer?.shop?.billing_address,
            payment_method: "Supply Chain Finance",
            payment_status: "Supply Chain Finance/Pending",
            delivery_method: "",
            discount: 0,
            order_status: "Pending",
            shipping_status: "Pending",
            total_amount: amount + gst + cess,
            gst: gst,
            cess_amount: cess,
            seller_mobile: "7498415258",
            source_city: "",
            destination_city: "",
            buyer_category_id: buyers?.find(
              (b) => b.id === values.selectedBuyer
            )?.user_category_id,
            seller_category_id:
              selectedInventory?.supplier?.user_kyc?.user_category_id,
            parent_order_id: Number(values.selectedBuyer),
            is_agree: true,
          },
        });
        if (order.data.order_id) {
          const inventory = await axios({
            method: "put",
            url: `/api/oms/${selectedInventory.id}/order`,
            data: {
              quantity: values.quantity,
              quality: selectedInventory.current_qp,
              order_id: order.data.order_id,
              out_time: new Date()
            },
          });
          if(!inventory?.data?.status) {
            toast.info(inventory?.data?.message || 'Error while updating inventory')
          }
          try {
            await axios({
              method: "post",
              url: `/ayekart_only_bnp/initial_email`,
              data: {
                id: `${buyerUserId}`,
                order_id: order.data.order_id,
                total_amount: `${amount + gst + cess}`,
              },
            });
          } catch {
            console.log("Error while adding only bnps or creating invoice");
          }

          try {
            await axios({
              method: "post",
              url: `/api1/invoice/discounting/invoice/create?order_id=${order.data.order_id}`,
            });
          } catch {
            console.log("Error while creating invoice");
          }

          try {
            await axios({
              method: "post",
              url: `/v3/api/sourcers`,
              data: {
                invoice_image_url: "",
                is_oms: true,
                kata_parchi_image_url: "",
                mandi_tax_url: "",
                merchant_type: "buyer",
                // mitra_user_id: null,
                order_id: order.data.order_id,
                bilty_image_url: "",
                sourcing_details: [
                  {
                    gst: gst,
                    product_id: selectedInventory?.product_id,
                    quantity: values.quantity,
                    rate: values.price,
                  }
                ],
                e_way_bill_image_url: "",
                total_cp: amount,
                truck_loading_image_url: "",
                user_id: buyerUserId,
              },
            });
          } catch {
            console.log(
              "Error while adding sourcing details to newly create order"
            );
          }

          toast.success(
            `Order placed successfully with ID: ${order.data.order_id}`,
            {
              position: "top-center",
              autoClose: "7000",
              closeButton: true,
            }
          );
          getData();
          handleClose();
          setIsSubmitting(false);
        } else {
          setIsSubmitting(false);
          toast.error("Error while placing the order", {
            position: "top-center",
          });
        }
      } else {
        setIsSubmitting(false);
        // Error while creating cart
        toast.error("Error while placing the order", {
          position: "top-center",
        });

        console.log("Error while creating cart");
      }
    } catch (err) {
      setIsSubmitting(false);
      console.log("Error while placing order");
      toast.error("Error while placing the order", { position: "top-center" });
      // Error handling
    }
  };

  const initialValues = {
    selectedBuyer: "",
    quantity: "",
    price: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validate: (values) => {
      const errors = {};
      const availableQuantity =
        Number(selectedInventory?.available_quantity) || 0;

      if (!values.selectedBuyer) {
        errors.selectedBuyer = "Please select a buyer";
      }
      if (!values.quantity) {
        errors.quantity = "Please enter a quantity";
      }
      if (values.quantity && values.quantity > availableQuantity) {
        errors.quantity = "Quantity should not be more than available quantity";
      }
      if (!values.price) {
        errors.price = "Please enter a price";
      }
      return errors;
    },
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      quantity: Number(selectedInventory?.available_quantity) || "",
      price: Number(selectedInventory?.product?.selling_price) || "",
    });
  }, [selectedInventory]);

  return (
    <div>
      <Modal
        open
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box sx={{ ...style }}>
          <h5 id="child-modal-title">
            Place Order For: {selectedInventory?.product?.product_name}
            {" | "}
            Available Quantity:{" "}
            {Number(selectedInventory?.available_quantity) || 0}
          </h5>
          <hr className="font-bold text-neutral-500 bg-gray-900 h-0.5" />
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-3">
              <div className="m-2">
                <Select
                  placeholder="Select Parent Order"
                  options={buyerOptions}
                  onChange={({ value }) => {
                    formik.setFieldValue("selectedBuyer", value);
                  }}
                  value={buyerOptions?.find(
                    (bo) => bo.id === formik.values.selectedBuyer
                  )}
                />
                {formik.errors.selectedBuyer && formik.touched.selectedBuyer ? (
                  <div className="text-red-600 text-sm flex justify-start ms-1">
                    {formik.errors.selectedBuyer}
                  </div>
                ) : null}
              </div>
              <div className="mb-2">
                <TextField
                  label="Quantity"
                  name="quantity"
                  type="number" onWheel={(e) => e.target.blur()}
                  inputProps={{ min: 0 }}
                  onChange={formik.handleChange}
                  value={formik.values.quantity}
                />
                {formik.errors.quantity && formik.touched.quantity ? (
                  <div className="text-red-600 text-sm flex justify-start ms-1">
                    {formik.errors.quantity}
                  </div>
                ) : null}
              </div>
              <div className="mb-2">
                <TextField
                  label="Price"
                  name="price"
                  type="number" onWheel={(e) => e.target.blur()}
                  inputProps={{ min: 0 }}
                  onChange={formik.handleChange}
                  value={formik.values.price}
                />
                {formik.errors.price && formik.touched.price ? (
                  <div className="text-red-600 text-sm flex justify-start ms-1">
                    {formik.errors.price}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-3 flex text-center justify-center">
              <Button
                type="submit"
                variant="contained"
                className="me-3"
                style={{ ...(!isSubmitting && { backgroundColor: "#131542" }) }}
                disabled={isSubmitting}
              >
                Place Order
              </Button>
              <Button
                variant="contained"
                type="button"
                style={{ backgroundColor: "#C7081E" }}
                onClick={() => handleClose()}
              >
                Close
              </Button>
            </div>
          </form>
        </Box>
      </Modal>
    </div>
  );
}

export default PlaceOrderInventory;
